import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, filter, tap } from 'rxjs';
import {
  Asset,
  HierarchyItem,
  HierarchyType,
  PauseAlertingWindow,
  Site,
  Sublocation
} from '../models/hierarchy.model';
import { HierarchyService } from './hierarchy.service';

@Injectable({
  providedIn: 'root'
})
export class BannerService {
  private alertingPausedItemSubject = new BehaviorSubject<HierarchyItem | null>(
    null
  );
  alertingPausedItem$ = this.alertingPausedItemSubject.asObservable();
  alertingPausePendingSubject = new BehaviorSubject<boolean>(false);
  alertingPausePending$ = this.alertingPausePendingSubject.asObservable();
  private currentPage = '';

  constructor(
    private activatedRoute: Router,
    private hierarchyService: HierarchyService
  ) {
    this.activatedRoute.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        tap((e) => {
          const event = e as NavigationStart;
          const page = event.url.split('/')[1] ?? '';
          const itemId = event.url.split('/')[2] ?? '';

          if (this.currentPage !== page || (page === 'assets' && !itemId)) {
            this.currentPage = page;
            this.alertingPausedItemSubject.next(null);
          }
        })
      )
      .subscribe();
  }

  setAlertingPausedItem(item: HierarchyItem | null) {
    if (!item) {
      this.alertingPausedItemSubject.next(item);

      return;
    }

    switch (item.type) {
      case HierarchyType.Site: {
        const { pauseAlertingWindow } = item as Site,
          isActive = this.alertingWindowIsActive(pauseAlertingWindow),
          isPending = this.alertingWindowIsPending(pauseAlertingWindow);

        this.alertingPausedItemSubject.next(
          isActive || isPending ? item : null
        );
        this.alertingPausePendingSubject.next(isPending);
        break;
      }
      case HierarchyType.Sublocation: {
        const { pauseAlertingWindow } = item as Sublocation,
          site = this.hierarchyService
            .queryAllOfLevel(HierarchyType.Site)
            .find(
              (s) => s.id === item.breadcrumb?.[HierarchyType.Site].id
            )! as Site;

        if (this.alertingWindowIsActive(site.pauseAlertingWindow)) {
          this.alertingPausedItemSubject.next(site);
          this.alertingPausePendingSubject.next(false);
        } else if (this.alertingWindowIsActive(pauseAlertingWindow)) {
          this.alertingPausedItemSubject.next(item);
          this.alertingPausePendingSubject.next(false);
        } else if (this.alertingWindowIsPending(site.pauseAlertingWindow)) {
          this.alertingPausedItemSubject.next(site);
          this.alertingPausePendingSubject.next(true);
        } else if (this.alertingWindowIsPending(pauseAlertingWindow)) {
          this.alertingPausedItemSubject.next(item);
          this.alertingPausePendingSubject.next(true);
        } else {
          this.alertingPausedItemSubject.next(null);
        }

        break;
      }
      case HierarchyType.Asset: {
        const { pauseAlertingWindow } = item as Asset,
          site = this.hierarchyService
            .queryAllOfLevel(HierarchyType.Site)
            .find(
              (s) => s.id === item.breadcrumb?.[HierarchyType.Site].id
            )! as Site,
          sublocation = this.hierarchyService
            .queryAllOfLevel(HierarchyType.Sublocation)
            .find(
              (s) => s.id === item.breadcrumb?.[HierarchyType.Sublocation].id
            )! as Sublocation;

        if (this.alertingWindowIsActive(site.pauseAlertingWindow)) {
          this.alertingPausedItemSubject.next(site);
          this.alertingPausePendingSubject.next(false);
        } else if (
          this.alertingWindowIsActive(sublocation.pauseAlertingWindow)
        ) {
          this.alertingPausedItemSubject.next(sublocation);
          this.alertingPausePendingSubject.next(false);
        } else if (this.alertingWindowIsActive(pauseAlertingWindow)) {
          this.alertingPausedItemSubject.next(item);
          this.alertingPausePendingSubject.next(false);
        } else if (this.alertingWindowIsPending(site.pauseAlertingWindow)) {
          this.alertingPausedItemSubject.next(site);
          this.alertingPausePendingSubject.next(true);
        } else if (
          this.alertingWindowIsPending(sublocation.pauseAlertingWindow)
        ) {
          this.alertingPausedItemSubject.next(sublocation);
          this.alertingPausePendingSubject.next(true);
        } else if (this.alertingWindowIsPending(pauseAlertingWindow)) {
          this.alertingPausedItemSubject.next(item);
          this.alertingPausePendingSubject.next(true);
        } else {
          this.alertingPausedItemSubject.next(null);
        }

        break;
      }
      default:
        this.setAlertingPausedItem(null);
        break;
    }
  }

  alertingWindowIsActive({ start, end }: PauseAlertingWindow): boolean {
    return (
      start < end &&
      Date.now() >= start.getTime() &&
      Date.now() <= end.getTime()
    );
  }

  alertingWindowIsPending({ start, end }: PauseAlertingWindow): boolean {
    return start < end && Date.now() < start.getTime();
  }
}
